<template>
  <div :class="containerClass" :style="containerStyle">
    <template v-if="hasRowspan">
      <!-- CSS Grid layout for rowspan support -->
      <MPanel v-for="(panel, index) in flattenedPanels" :key="`page-${pageId}-panel-${panel.panelId || index}`" :data="panel"
        :page-id="pageId" :style="getPanelGridStyle(panel)" class="grid-panel" />
    </template>
    <template v-else>
      <!-- Original flexbox layout -->
      <MRow
        v-for="(panelRow, i) in rows"
        :key="`page-${pageId}-row-${i}`"
        :class="{ 'panels-flex-row--grow': getRowGrowBehavior(panelRow) }"
      >
        <template v-for="(panel, j) in panelRow" :key="`page-${pageId}-cell-${i}-${j}`">
          <MPanel
            v-if="panel && !panel._isRowspanPlaceholder"
            :key="`page-${pageId}-panel-${panel.panelId || `${i}-${j}`}`"
            :data="panel"
            :page-id="pageId"
          />
        </template>
      </MRow>
    </template>
  </div>
</template>

<script setup lang="ts">
import MRow from '@/components/ui/MRow.vue'
import { computed, onMounted, provide } from 'vue'
import MPanel from './Panel.vue'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'

interface Props {
  rows: any[]
  lazy?: boolean
  pageId: string | number
}

const props = defineProps<Props>()

const panelCount = computed(() =>
  props.rows.reduce((count, row) => {
    if (!Array.isArray(row)) return count
    return count + row.filter((panel: any) => panel && !panel._isRowspanPlaceholder).length
  }, 0),
)

const elementCount = computed(() =>
  props.rows.reduce((count, row) => {
    if (!Array.isArray(row)) return count
    return count + row.reduce((innerCount: number, panel: any) => {
      if (!panel?.rows || !Array.isArray(panel.rows)) return innerCount
      return innerCount + panel.rows.reduce((rowCount: number, elementRow: any) => {
        if (!Array.isArray(elementRow)) return rowCount
        return rowCount + elementRow.filter((cell: any) => cell?.element).length
      }, 0)
    }, 0)
  }, 0),
)

onMounted(() => {
  startWorkflowMeasure('PanelsBlock.onMounted', {
    pageId: props.pageId,
    rowCount: props.rows.length,
    panelCount: panelCount.value,
    elementCount: elementCount.value,
    hasRowspan: hasRowspan.value,
  })()
})

// Check if any panel has rowspan (must be defined before provide)
const hasRowspan = computed(() => {
  return props.rows.some(row =>
    row && row.some((panel: any) => panel && panel._attributes?.rowspan && parseInt(panel._attributes.rowspan) > 1)
  )
})

// Provide для lazy загрузки
provide('isPageLazy', () => props.lazy || false)
provide('useGridLayout', hasRowspan)

// Flatten panels with grid position info
// Also filters out null/undefined panels and placeholders
const flattenedPanels = computed(() => {
  const panels: any[] = []
  props.rows.forEach((row, rowIndex) => {
    if (!row) return
    row.forEach((panel: any, colIndex: number) => {
      // Filter out null, undefined, and rowspan placeholders
      if (!panel || panel._isRowspanPlaceholder) {
        return
      }
      panels.push({
        ...panel,
        _gridRow: rowIndex + 1,
        _gridColumn: colIndex + 1
      })
    })
  })
  return panels
})

// Calculate grid template columns from first row
const gridTemplateColumns = computed(() => {
  if (!props.rows || props.rows.length === 0) return 'none'
  const firstRow = props.rows[0] || []

  return firstRow
    .filter((panel: any) => panel != null)
    .map((panel: any) => {
      const cols = panel._attributes?.cols || 100
      return `${cols}%`
    }).join(' ')
})

// Check if a row should grow vertically based on its panels' content
const getRowGrowBehavior = (row: any[]) => {
  if (!row) return false

  return row.some((panel: any) => {
    if (!panel || panel._isRowspanPlaceholder) return false

    // Check if panel has rows with elements
    if (panel.rows && Array.isArray(panel.rows)) {
      return panel.rows.some((elemRow: any) => {
        if (!Array.isArray(elemRow)) return false
        return elemRow.some((cell: any) => {
          if (!cell || !cell.element) return false
          // Check grabVertical attribute
          const grabVertical = cell.element.grabVertical?._text
          return grabVertical === 'true' || grabVertical === '1'
        })
      })
    }
    return false
  })
}

// Calculate grid template rows - use 1fr for rows that want to grow, auto for others
const gridTemplateRows = computed(() => {
  if (!props.rows || props.rows.length === 0) return 'none'

  return props.rows.map(row => {
    const shouldGrow = getRowGrowBehavior(row)
    return shouldGrow ? '1fr' : 'auto'
  }).join(' ')
})

const containerClass = computed(() => ({
  'panels-grid': hasRowspan.value,
  'panels-flex': !hasRowspan.value,
}))

const containerStyle = computed(() => {
  if (!hasRowspan.value) return {}

  const hasAnyGrowingRow = props.rows.some(row => getRowGrowBehavior(row))

  return {
    display: 'grid',
    gridTemplateColumns: gridTemplateColumns.value,
    gridTemplateRows: gridTemplateRows.value,
    gap: '0.5rem',
    height: hasAnyGrowingRow ? '100%' : 'auto'
  }
})

const getPanelGridStyle = (panel: any) => {
  const rowspan = parseInt(panel._attributes?.rowspan) || 1
  const rowIndex = panel._gridRow - 1

  // Check if panel's row should grow
  const shouldGrow = getRowGrowBehavior(props.rows[rowIndex])

  return {
    gridRow: `${panel._gridRow} / span ${rowspan}`,
    gridColumn: panel._gridColumn,
    height: shouldGrow ? '100%' : 'auto'
  }
}
</script>

<style scoped>
.panels-flex,
.panels-grid {
  width: 100%;
}

.panels-flex {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
}

.panels-flex :deep(.flex) {
  width: 100%;
}

.panels-flex :deep(.flex.panels-flex-row--grow) {
  flex: 1 1 auto;
  min-height: 0;
}

.grid-panel {
  min-height: 0;
}
</style>
